import React from 'react'

const FeaturedReleaseCard = ({ featuredRelease, userData, formatDate, formatTime }) => {
  // Icon components
  const GiftIcon = () => (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <rect x="3" y="8" width="18" height="4" rx="1"/>
      <rect x="4" y="12" width="16" height="9" rx="1"/>
      <path d="M12 8V4"/>
      <path d="M8 8V4a2 2 0 0 1 4 0"/>
      <path d="M16 8V4a2 2 0 0 0-4 0"/>
    </svg>
  );

  const StarIcon = ({ filled = false }) => (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 24 24" 
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
    </svg>
  );

  // Get primary artist from release data
  const getPrimaryArtist = () => {
    if (featuredRelease.release_artist && featuredRelease.release_artist.length > 0) {
      const primaryArtists = featuredRelease.release_artist
        .filter(artist => artist.role === 'Primary Artist')
        .map(artist => artist.artist.name);
      return primaryArtists.join(', ') || 'Various Artists';
    }
    return userData?.artists?.[0]?.name || 'Various Artists';
  };

  return (
    <div className="featured-release-section">
      <div className="featured-release-card">
        {/* Background with artwork overlay */}
        <div className="card-background" style={{
          backgroundImage: featuredRelease.artwork ? `url(${featuredRelease.artwork})` : 'none'
        }}>
          {/* Overlay for better text readability */}
          <div className="background-overlay"></div>

          {/* Cloud-like soft background elements */}
          <div className="background-clouds">
            <div className="cloud cloud-1"></div>
            <div className="cloud cloud-2"></div>
            <div className="cloud cloud-3"></div>
            <div className="cloud cloud-4"></div>
          </div>

          {/* Detailed floating decorative elements */}
          <div className="floating-elements">
            {/* Flower petals */}
            <div className="petal petal-1"></div>
            <div className="petal petal-2"></div>
            <div className="petal petal-3"></div>
            <div className="petal petal-4"></div>
            <div className="petal petal-5"></div>
            <div className="petal petal-6"></div>
            <div className="petal petal-7"></div>
            <div className="petal petal-8"></div>
            <div className="petal petal-9"></div>
            <div className="petal petal-10"></div>

            {/* Gift boxes */}
            <div className="gift-box gift-1">
              <GiftIcon />
            </div>
            <div className="gift-box gift-2">
              <GiftIcon />
            </div>

            {/* Stars */}
            <div className="star star-1">
              <StarIcon filled />
            </div>
            <div className="star star-2">
              <StarIcon filled />
            </div>
            <div className="star star-3">
              <StarIcon filled />
            </div>
            <div className="star star-4">
              <StarIcon filled />
            </div>
            <div className="star star-5">
              <StarIcon filled />
            </div>

            {/* Plus signs */}
            <div className="plus plus-1">+</div>
            <div className="plus plus-2">+</div>
            <div className="plus plus-3">+</div>
            <div className="plus plus-4">+</div>

            {/* Diamond shapes */}
            <div className="diamond diamond-1"></div>
            <div className="diamond diamond-2"></div>
            <div className="diamond diamond-3"></div>

            {/* Hearts */}
            <div className="heart heart-1">♥</div>
            <div className="heart heart-2">♥</div>
            <div className="heart heart-3">♥</div>
          </div>

          {/* Top section with release info */}
          <div className="release-header">
            <div className="release-time-info">
              <div className="duration">
                {featuredRelease.genre || featuredRelease.primaryGenre || 'Music'}
              </div>
              <div className="status-badge">
                🟢 {featuredRelease.status}
              </div>
              <div className="date-info">
                <div className="label">Release Date</div>
                <div className="value">{formatDate(featuredRelease.originalDate)}</div>
              </div>
            </div>
          </div>

          {/* Center content area - removed cassette */}
          <div className="release-center">
            <div className="release-artwork">
              <img 
                src={featuredRelease.artwork || "/api/placeholder/200/200"} 
                alt={`${featuredRelease.title} artwork`}
                className="artwork-image"
              />
            </div>
          </div>

          {/* Bottom section with enhanced typography */}
          <div className="release-footer">
            <div className="release-title-section">
              <h2 className="release-title">{featuredRelease.title}</h2>
              <p className="release-artist">{getPrimaryArtist()}</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .featured-release-card {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          height: 100%;
        }

        .card-background {
          position: relative;
          height: 384px;
          background: linear-gradient(to bottom right, #fce7f3, #e879f9, #60a5fa, #6366f1);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          padding: 24px;
        }

        .background-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(252, 231, 243, 0.8) 0%,
            rgba(232, 121, 249, 0.7) 25%,
            rgba(96, 165, 250, 0.7) 75%,
            rgba(99, 102, 241, 0.8) 100%
          );
          z-index: 1;
        }

        .background-clouds {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2;
        }

        .floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 3;
        }

        .release-header {
          position: relative;
          display: flex;
          justify-content: flex-start;
          align-items: flex-start;
          margin-bottom: 20px;
          z-index: 10;
        }

        .release-time-info {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
        }

        .duration,
        .status-badge {
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .date-info {
          text-align: center;
        }

        .date-info .label {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.8);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 2px;
          font-weight: 500;
        }

        .date-info .value {
          font-size: 12px;
          color: white;
          font-weight: 600;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .release-center {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          z-index: 10;
        }

        .release-artwork {
          position: relative;
          transform: perspective(500px) rotateY(-8deg) rotateX(2deg);
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3));
        }

        .artwork-image {
          width: 120px;
          height: 120px;
          border-radius: 12px;
          object-fit: cover;
          border: 3px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
        }

        .release-footer {
          position: relative;
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;
          margin-top: auto;
          z-index: 10;
        }

        .release-title-section {
          flex: 1;
        }

        .release-title {
          font-size: 24px;
          font-weight: 800;
          color: white;
          margin: 0 0 4px 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          line-height: 1.2;
        }

        .release-artist {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
          font-weight: 500;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }

        .cloud {
          position: absolute;
          border-radius: 50%;
          filter: blur(40px);
        }

        .cloud-1 {
          top: 0;
          left: 0;
          width: 128px;
          height: 128px;
          background: linear-gradient(to bottom right, rgba(252, 231, 243, 0.4), transparent);
        }

        .cloud-2 {
          top: 64px;
          right: 0;
          width: 160px;
          height: 160px;
          background: linear-gradient(to bottom left, rgba(233, 213, 255, 0.3), transparent);
          filter: blur(80px);
        }

        .cloud-3 {
          bottom: 0;
          left: 32px;
          width: 144px;
          height: 144px;
          background: linear-gradient(to top right, rgba(186, 230, 253, 0.4), transparent);
          filter: blur(40px);
        }

        .cloud-4 {
          bottom: 32px;
          right: 0;
          width: 112px;
          height: 112px;
          background: linear-gradient(to top left, rgba(249, 168, 212, 0.5), transparent);
          filter: blur(32px);
        }

        .petal {
          position: absolute;
          border-radius: 50%;
          opacity: 0.8;
        }

        .petal-1 {
          top: 48px;
          left: 48px;
          width: 24px;
          height: 12px;
          background: linear-gradient(to right, #f9a8d4, #fb7185);
          transform: rotate(45deg);
        }

        .petal-2 {
          top: 32px;
          left: 64px;
          width: 16px;
          height: 8px;
          background: linear-gradient(to right, #fb7185, #f43f5e);
          transform: rotate(12deg);
          opacity: 0.7;
        }

        .petal-3 {
          top: 80px;
          right: 64px;
          width: 20px;
          height: 12px;
          background: linear-gradient(to right, #fed7aa, #fb923c);
          transform: rotate(-30deg);
          opacity: 0.75;
        }

        .petal-4 {
          top: 64px;
          right: 48px;
          width: 12px;
          height: 8px;
          background: linear-gradient(to right, #fb923c, #f97316);
          transform: rotate(60deg);
        }

        .petal-5 {
          top: 128px;
          left: 32px;
          width: 16px;
          height: 8px;
          background: linear-gradient(to right, #fde047, #facc15);
          transform: rotate(90deg);
          opacity: 0.7;
        }

        .petal-6 {
          top: 112px;
          left: 48px;
          width: 24px;
          height: 12px;
          background: linear-gradient(to right, #fef3c7, #fde047);
          transform: rotate(-15deg);
          opacity: 0.6;
        }

        .petal-7 {
          bottom: 128px;
          right: 48px;
          width: 20px;
          height: 12px;
          background: linear-gradient(to right, #d8b4fe, #c084fc);
          transform: rotate(45deg);
          opacity: 0.75;
        }

        .petal-8 {
          bottom: 112px;
          right: 32px;
          width: 16px;
          height: 8px;
          background: linear-gradient(to right, #c084fc, #a855f7);
          transform: rotate(-60deg);
        }

        .petal-9 {
          bottom: 160px;
          left: 64px;
          width: 24px;
          height: 12px;
          background: linear-gradient(to right, #fce7f3, #f9a8d4);
          transform: rotate(30deg);
          opacity: 0.65;
        }

        .petal-10 {
          bottom: 144px;
          left: 48px;
          width: 12px;
          height: 8px;
          background: linear-gradient(to right, #f9a8d4, #fb7185);
          transform: rotate(-45deg);
          opacity: 0.7;
        }

        .gift-box {
          position: absolute;
          color: rgba(252, 231, 243, 0.7);
        }

        .gift-1 {
          top: 96px;
          right: 80px;
          transform: rotate(12deg);
        }

        .gift-2 {
          bottom: 192px;
          left: 80px;
          transform: rotate(-30deg);
          opacity: 0.6;
        }

        .star {
          position: absolute;
          color: white;
        }

        .star-1 {
          top: 64px;
          right: 96px;
          opacity: 0.9;
        }

        .star-2 {
          top: 112px;
          left: 80px;
          color: #fde047;
          opacity: 0.8;
        }

        .star-3 {
          top: 160px;
          right: 32px;
          opacity: 0.7;
        }

        .star-4 {
          bottom: 176px;
          right: 80px;
          color: #fce7f3;
          opacity: 0.85;
        }

        .star-5 {
          bottom: 128px;
          left: 96px;
          opacity: 0.75;
        }

        .plus {
          position: absolute;
          color: white;
          font-size: 18px;
          font-weight: 300;
          opacity: 0.6;
        }

        .plus-1 {
          top: 144px;
          left: 16px;
        }

        .plus-2 {
          top: 176px;
          right: 16px;
          color: #fce7f3;
          font-size: 14px;
          opacity: 0.7;
        }

        .plus-3 {
          bottom: 208px;
          left: 32px;
          font-size: 16px;
          opacity: 0.5;
        }

        .plus-4 {
          bottom: 80px;
          right: 96px;
          color: #d8b4fe;
          opacity: 0.6;
        }

        .diamond {
          position: absolute;
          width: 8px;
          height: 8px;
          transform: rotate(45deg);
        }

        .diamond-1 {
          top: 208px;
          left: 24px;
          background: #f9a8d4;
          opacity: 0.7;
        }

        .diamond-2 {
          bottom: 96px;
          right: 24px;
          width: 12px;
          height: 12px;
          background: #d8b4fe;
          opacity: 0.6;
        }

        .diamond-3 {
          top: 80px;
          left: 128px;
          background: #fde047;
          opacity: 0.8;
        }

        .heart {
          position: absolute;
          color: #f9a8d4;
          font-size: 14px;
          opacity: 0.7;
        }

        .heart-1 {
          top: 192px;
          right: 48px;
        }

        .heart-2 {
          bottom: 224px;
          left: 80px;
          color: #fce7f3;
          font-size: 12px;
          opacity: 0.6;
        }

        .heart-3 {
          top: 240px;
          left: 112px;
          color: #d8b4fe;
          opacity: 0.65;
        }
      `}</style>
    </div>
  )
}

export default FeaturedReleaseCard 